import React from 'react';
import NotificationSystem from 'react-notification-system';
import classNames from 'classnames';

import Toast from './Toast';
import SnackBar from './SnackBar';
import getPropsForComponent from '@adsk/bim360-matrix-react-helpers/es/getPropsForComponent';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { ThemeConsumer } from '@adsk/bim360-matrix-react-helpers/es/theme';
import { NotificationLevels, NotificationTypes } from './helpers/consts';

import './_NotificationManager.scss';

export { NotificationLevels, NotificationTypes };

const NOTIFICATION_AUTO_DISMISS = 6,
  NOTIFICATION_DONT_DISMISS = 0;

/**
 * @class NotificationManager

The NotificationManager component holds basic functionality for handling notifications.
Calling `addNotification` function will show a notification.
You can choose between 2 types of notifications: Toast & Snackbar.

#### NotificationTypes

Exported as NotificationTypes from this package.

1. Toast - appears on the top righthand side of the page. (Multiple Toasts can appear simultaneously)
2. Snackbar - appears at the top-center part of the page. (Only one Snackbar can appear at a time - The newer will override the older)

#### NotificationLevels

Exported as NotificationLevels from this package.

Values - INFO/SUCCESS/ERROR/WARNING

# Usage Example (How we use it at Field, you may want to make your own implementation)

 For a hooks based implementation - see `helpers/context.js`.

1. Render NotificationManager at the Main.js file so it is always on the page. (Not visible, but rendered)
    ```javascript
    <NotificationManager ref={(n) => { this.notificationManager = n; }}/>
    ```
2. Keep a notificationReducer.js file, which adds a `notificationComponent` object in the Redux store whenever a relevant action is dispatched (further detailed below):
    ```javascript
    import * as Immutable from 'seamless-immutable';
    import get from 'lodash/get';
    import { NotificationTypes, NotificationLevels } from '@adsk/bim360-matrix-react-components';
    import * as Actions from '@adsk/bim360-matrix-react-action-names';

    // These are the parameters to build the notification data
    // notificationComponent - Takes the name of the component that returns the data that would be shown in the notification
    // level - A value from NotificationLevels
    // props - Optional object that would be passed as props to the notificationComponent
    // autoDismiss - How many seconds the notification will be shown for before disappearing. Defaults to 6
    // type - A value from NotificationTypes
    // onDismiss - An optional callback function for notification dismiss
    const initialState = Immutable.from({
       notificationComponent: '',
       level: '',
       props: {},
       autoDismiss: 6,
       type: '',
       onDismiss: () => {}
    });

    const notificationManagerReducer = (state = initialState, action) => {
        switch (action.type) {
          case Actions.PERMISSIONS_FAILED:
            return Immutable.from(Object.assign({}, state, {
              notificationComponent: 'TextNotification', // TextNotification is the class name of a very simple component to return text, code shared below
              level: NotificationLevels.ERROR,
              props: { translationKey: action.payload }, // These props will be passed to "TextNotification" component in this example, since this is what was passed to 'notificationComponent'
              type: NotificationTypes.SNACKBAR
            }));

           .... further cases ...

           // Explaining below why we have this 'case'
           case Actions.RESET_NOTIFICATION:
             return Immutable.from(initialState);
           }
           default:
             return state;
           }
        }
    });
    ```

3. In Main.js file (where the NotificationManager is rendered as explain in step #1) listen for the Redux store changes, e.g. in `componentDidUpdate`, and do the following:

    ```javascript
    import * as Notifications from './notifications';

    ...

    // in componentDidUpdate
    if (this.props.notification.notificationComponent !== '' && this.props.notification !== prevProps.notification) {
       // Notifications is our own array of components to match what we passed in to `notificationComponent` the reducer above ('TextNotification' in this example).
       const NotificationComponent = Notifications[this.props.notification.notificationComponent];

       // Triggers the notification
       this.notificationManager.addNotification({
         level: this.props.notification.level,
         autoDismiss: this.props.notification.autoDismiss,
         type: this.props.notification.type,
         message: (<NotificationComponent {...this.props.notification.props}/>)
         onDismiss: this.props.notification.onDismiss
       });

       // Resetting the notification in the store so that components that rely on the the notification in the store will be updated and won't "remember" an unrelated notification.
       this.props.resetNotification();
    }
    ```

4. Dispatch a notification action. This will trigger the reducer to modify the Redux store, which will be picked up in Main.js and will call the `addNotification` function:

     ```javascript
     this.props.showNotification({ type: Actions.PERMISSIONS_FAILED }); // Assuming showNotification is mapped using mapDispatchToProps in your component
     ```

### Example of our `TextNotification` component:

    import React from 'react';
    import PropTypes from 'prop-types';
    import T from 'i18n-react';

    const TextNotification = ({ translationKey, translationParameters }) => <div>{ T.translate(translationKey, translationParameters) }</div>;

    TextNotification.propTypes = {
      translationKey: PropTypes.string.isRequired,
      translationParameters: PropTypes.object
    };

    export default TextNotification;

### Example of our 'GenericNotificationWithLink' component: (renders a notification with a clickable link)

    import React from 'react';
    import PropTypes from 'prop-types';
    import T from 'i18n-react';

    import { pushState, replaceState } from '@adsk/bim360-matrix-react-helpers/es/navigator';
    import { getAdjustedPathname } from '@adsk/bim360-matrix-react-helpers/es/urlHelper';
    import { AnchorButton } from '@adsk/bim360-matrix-react-components';


    const GenericNotificationWithLink = (props) => {

    const _handleOnClick = (link) => {
      if (getAdjustedPathname() !== link.pathname) {
        pushState(link.pathname, link.query);
      }
      else {
        replaceState(link.pathname, link.query);
      }
      props.closeNotification();
    };

    return (
      <div className="GENERIC_NOTIFICATION_WITH_LINK">
        { T.translate(props.translationKey, {
          link: <AnchorButton className="GENERIC_NOTIFICATION_WITH_LINK__inline-link"
            onClick={ () => _handleOnClick(props.link) }>{ props.linkText }
          </AnchorButton>
        })
        }
      </div>
    );
    };

    GenericNotificationWithLink.propTypes = {
      link: PropTypes.object.isRequired,
      linkText: PropTypes.string.isRequired,
      translationKey: PropTypes.string.isRequired,
      // closeNotification is passed by NotificationManager as part of React.cloneElement handling of notification message
      closeNotification: PropTypes.func.isRequired
    };

    export default GenericNotificationWithLink;

 */
export default class NotificationManager extends React.Component {
  /**
   * @constructor NotificationManager
   **/
  constructor(props) {
    super(props);

    this.state = {
      currentSnackBarNotification: null,
      showOverlay: false
    };

    this.setNotificationSystemRef = this.setNotificationSystemRef.bind(this);
  }

  /**
   *
   * @param notification
   */
  addNotification(notification) {
    if (!notification.type) {
      console.error('No notification type passed. Dropping notification...');
      return null;
    }

    let NotificationTypeComponent;
    let notificationPosition;
    switch (notification.type) {
      case NotificationTypes.TOAST:
        NotificationTypeComponent = Toast;
        // We're using br and not tr since the component animation for br is what we want and we're overriding br css
        notificationPosition = 'br';
        break;

      case NotificationTypes.SNACKBAR:
        // If there's already a snackbar notification, we're removing it since there should only be one snackbar at any time
        if (this.state.currentSnackBarNotification) {
          this.removeNotification(this.state.currentSnackBarNotification);
        }
        NotificationTypeComponent = SnackBar;
        // We're using bc and not tc since the component animation for bc is what we want and we're overriding bc css
        notificationPosition = 'bc';
        break;
      default:
        console.error(
          `Wrong notification type passed. Received '${
            notification.type
          }' while expected one of '${objectValues(NotificationTypes)}'. Dropping notification...`
        );
        return null;
    }

    const notificationLevel = notification.level || NotificationLevels.INFO;
    const hasPromise =
      notification.asyncOperation && typeof notification.asyncOperation.then === 'function';

    const autoDismiss =
      notification.dismissible === false || hasPromise
        ? NOTIFICATION_DONT_DISMISS
        : isNullOrUndefined(notification.autoDismiss)
          ? NOTIFICATION_AUTO_DISMISS
          : notification.autoDismiss;
    let internalNotification = null;
    const innerNotification = {
      // Defaults
      level: notificationLevel,
      // Notification specific values
      ...notification,
      // Overrides. Assigning dismissible false for removing the x in the upper left corner.
      autoDismiss,
      position: notificationPosition,
      action: null,
      dismissible: false,
      message: null,
      title: null,
      // We're passing removeNotification with internalNotification by reference since we're later on assigning the notification to internalNotification
      children: (
        <NotificationTypeComponent
          {...notification}
          level={notificationLevel}
          onDismiss={() => this.dismissNotification(internalNotification)}
          removeNotification={() => this.removeNotification(internalNotification)}
        />
      )
    };

    internalNotification = this.notificationSystem.addNotification(innerNotification);

    if (hasPromise && notification.dismissible !== false && notification.autoDismiss > 0) {
      // In case of successful run we want to show a success message & auto dismiss the toast.
      // If the promise is rejected we need to remove the toast as we might need to show a more serious error (snackbar, etc.)
      notification.asyncOperation
        .then(() => {
          setTimeout(() => {
            this.notificationSystem &&
              this.notificationSystem.removeNotification(internalNotification);
          }, notification.autoDismiss * 1000);
        })
        .catch(
          () =>
            this.notificationSystem &&
            this.notificationSystem.removeNotification(internalNotification)
        );
    }

    if (notification.type === NotificationTypes.SNACKBAR) {
      this.setState({
        currentSnackBarNotification: internalNotification,
        showOverlay: notification.dismissible === false
      });
    }

    return internalNotification;
  }

  /**
   *
   * @param notification
   */
  removeNotification(notification) {
    this.notificationSystem.removeNotification(notification);
    if (
      this.state.currentSnackBarNotification &&
      notification.uid === this.state.currentSnackBarNotification.uid
    ) {
      this.setState({ showOverlay: false, currentSnackBarNotification: null });
    }
  }

  /**
   *
   * @param notification
   */
  dismissNotification(notification) {
    notification.onDismiss && notification.onDismiss();
    this.removeNotification(notification);
  }

  /**
   *
   */
  clearNotifications() {
    this.notificationSystem.clearNotifications();
    this.state.showOverlay && this.setState({ showOverlay: false });
  }

  setNotificationSystemRef(ref) {
    this.notificationSystem = ref;
  }

  /**
   * Render
   * @returns {VDOM}
   **/
  render() {
    const { overlayClassName, ...restProps } = this.props;
    const notificationProps = {
      ...getPropsForComponent(restProps, NotificationSystem),
      allowHTML: false,
      style: false
    };

    return (
      <ThemeConsumer>
        {({ theme }) => (
          <div className={classNames('NotificationManager', `NotificationManager--${theme}`)}>
            {this.state.showOverlay && (
              <div className={`${classNames('NotificationManager__overlay', overlayClassName)}`} />
            )}
            <NotificationSystem {...notificationProps} ref={this.setNotificationSystemRef} />
          </div>
        )}
      </ThemeConsumer>
    );
  }
}

NotificationManager.propTypes = {
  ...NotificationSystem.propTypes
};
